<script setup>
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import BookmarksAPI from 'dashboard/api/bookmarks';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import BookmarkDrawer from './components/BookmarkDrawer.vue';

const { t } = useI18n();

const bookmarks = ref([]);
const loading = ref(false);
const searchQuery = ref('');
const viewMode = ref('grid'); // 'grid' ou 'list'
const sortBy = ref('updated_at');
const sortOrder = ref('desc');
const keywordFilter = ref('');
const selectedTags = ref([]);
const showSortMenu = ref(false);
const showFilterMenu = ref(false);
const openMenuId = ref(null);
const showEditModal = ref(false);
const showDeleteModal = ref(false);
const showCreateModal = ref(false);
const showBookmarkDrawer = ref(false);
const selectedBookmark = ref(null);
const bookmarkToEdit = ref(null);
const bookmarkToDelete = ref(null);
const deleteDialog = ref(null);
const editDialog = ref(null);
const createDialog = ref(null);

// Formulaire
const formTitle = ref('');
const formUrl = ref('');
const formDescription = ref('');
const formKeywords = ref('');
const formThumbnail = ref(null);
const thumbnailInput = ref(null);

// Extraire tous les tags uniques de tous les bookmarks
const allTags = computed(() => {
  const tagsSet = new Set();
  bookmarks.value.forEach(bookmark => {
    if (bookmark.keywords_array && Array.isArray(bookmark.keywords_array)) {
      bookmark.keywords_array.forEach(tag => {
        if (tag && tag.trim()) {
          tagsSet.add(tag.trim());
        }
      });
    }
  });
  return Array.from(tagsSet).sort();
});

const filteredBookmarks = computed(() => {
  let filtered = bookmarks.value;

  // Filtre par recherche
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase();
    filtered = filtered.filter(
      bookmark =>
        bookmark.title?.toLowerCase().includes(query) ||
        bookmark.description?.toLowerCase().includes(query) ||
        bookmark.url?.toLowerCase().includes(query) ||
        bookmark.keywords?.toLowerCase().includes(query)
    );
  }

  // Filtre par tags sélectionnés
  if (selectedTags.value.length > 0) {
    filtered = filtered.filter(bookmark => {
      if (!bookmark.keywords_array || !Array.isArray(bookmark.keywords_array)) {
        return false;
      }
      // Le bookmark doit avoir au moins un des tags sélectionnés
      return selectedTags.value.some(selectedTag =>
        bookmark.keywords_array.some(
          tag => tag && tag.trim().toLowerCase() === selectedTag.toLowerCase()
        )
      );
    });
  }

  // Filtre par keyword (ancien système, pour compatibilité)
  if (keywordFilter.value) {
    const keyword = keywordFilter.value.toLowerCase();
    filtered = filtered.filter(bookmark =>
      bookmark.keywords?.toLowerCase().includes(keyword)
    );
  }

  // Tri
  filtered = [...filtered].sort((a, b) => {
    let comparison = 0;

    switch (sortBy.value) {
      case 'title':
        comparison = (a.title || '').localeCompare(b.title || '');
        break;
      case 'url':
        comparison = (a.url || '').localeCompare(b.url || '');
        break;
      case 'updated_at':
      default:
        comparison = new Date(a.updated_at) - new Date(b.updated_at);
        break;
    }

    return sortOrder.value === 'asc' ? comparison : -comparison;
  });

  return filtered;
});

// Récupérer tous les keywords uniques
const allKeywords = computed(() => {
  const keywordSet = new Set();
  bookmarks.value.forEach(bookmark => {
    if (bookmark.keywords_array && Array.isArray(bookmark.keywords_array)) {
      bookmark.keywords_array.forEach(kw => {
        if (kw && kw.trim()) {
          keywordSet.add(kw.trim());
        }
      });
    } else if (bookmark.keywords) {
      bookmark.keywords.split(',').forEach(kw => {
        if (kw && kw.trim()) {
          keywordSet.add(kw.trim());
        }
      });
    }
  });
  return Array.from(keywordSet).sort();
});

const isEmpty = computed(() => !loading.value && bookmarks.value.length === 0);

const fetchBookmarks = async () => {
  loading.value = true;
  try {
    const response = await BookmarksAPI.getAll({
      sort_by: sortBy.value,
      sort_order: sortOrder.value,
      search: searchQuery.value || undefined,
      keyword: keywordFilter.value || undefined,
    });
    bookmarks.value = response.data;
  } catch (error) {
    useAlert(t('BOOKMARKS.API.ERROR.FETCH_FAILED'));
  } finally {
    loading.value = false;
  }
};

const toggleMenu = (bookmarkId, event) => {
  event?.stopPropagation();
  openMenuId.value = openMenuId.value === bookmarkId ? null : bookmarkId;
};

const closeMenu = () => {
  openMenuId.value = null;
};

const openCreateModal = async () => {
  resetForm();
  showCreateModal.value = true;
  await nextTick();
  createDialog.value?.open();
};

const closeCreateModal = () => {
  showCreateModal.value = false;
  resetForm();
};

const openEditModal = async bookmark => {
  bookmarkToEdit.value = bookmark;
  formTitle.value = bookmark.title || '';
  formUrl.value = bookmark.url || '';
  formDescription.value = bookmark.description || '';
  formKeywords.value = Array.isArray(bookmark.keywords_array)
    ? bookmark.keywords_array.join(', ')
    : bookmark.keywords || '';
  formThumbnail.value = null;
  showEditModal.value = true;
  await nextTick();
  editDialog.value?.open();
};

const closeEditModal = () => {
  showEditModal.value = false;
  bookmarkToEdit.value = null;
  resetForm();
};

const openDeleteModal = async bookmark => {
  bookmarkToDelete.value = bookmark;
  showDeleteModal.value = true;
  await nextTick();
  deleteDialog.value?.open();
};

const closeDeleteModal = () => {
  showDeleteModal.value = false;
  bookmarkToDelete.value = null;
};

const resetForm = () => {
  formTitle.value = '';
  formUrl.value = '';
  formDescription.value = '';
  formKeywords.value = '';
  formThumbnail.value = null;
  if (thumbnailInput.value) {
    thumbnailInput.value.value = '';
  }
};

const createBookmark = async () => {
  if (!formTitle.value || !formUrl.value) {
    useAlert(t('BOOKMARKS.API.ERROR.REQUIRED_FIELDS'));
    return;
  }

  try {
    const formData = new FormData();
    formData.append('bookmark[title]', formTitle.value);
    formData.append('bookmark[url]', formUrl.value);
    formData.append('bookmark[description]', formDescription.value);
    formData.append('bookmark[keywords]', formKeywords.value);
    if (formThumbnail.value) {
      formData.append('thumbnail', formThumbnail.value);
    }

    await BookmarksAPI.create(formData);
    useAlert(t('BOOKMARKS.API.SUCCESS.BOOKMARK_CREATED'));
    await fetchBookmarks();
    closeCreateModal();
  } catch (error) {
    useAlert(t('BOOKMARKS.API.ERROR.CREATE_FAILED'));
  }
};

const updateBookmark = async () => {
  if (!formTitle.value || !formUrl.value) {
    useAlert(t('BOOKMARKS.API.ERROR.REQUIRED_FIELDS'));
    return;
  }

  try {
    const formData = new FormData();
    formData.append('bookmark[title]', formTitle.value);
    formData.append('bookmark[url]', formUrl.value);
    formData.append('bookmark[description]', formDescription.value);
    formData.append('bookmark[keywords]', formKeywords.value);
    if (formThumbnail.value) {
      formData.append('thumbnail', formThumbnail.value);
    }

    await BookmarksAPI.update(bookmarkToEdit.value.id, formData);
    useAlert(t('BOOKMARKS.API.SUCCESS.BOOKMARK_UPDATED'));
    await fetchBookmarks();
    closeEditModal();
  } catch (error) {
    useAlert(t('BOOKMARKS.API.ERROR.UPDATE_FAILED'));
  }
};

const deleteBookmark = async () => {
  try {
    await BookmarksAPI.delete(bookmarkToDelete.value.id);
    useAlert(t('BOOKMARKS.API.SUCCESS.BOOKMARK_DELETED'));
    await fetchBookmarks();
    closeDeleteModal();
  } catch (error) {
    useAlert(t('BOOKMARKS.API.ERROR.DELETE_FAILED'));
  }
};

const handleThumbnailChange = event => {
  const file = event.target.files[0];
  if (file) {
    formThumbnail.value = file;
  }
};

const handleImageError = event => {
  // Si l'image ne charge pas, masquer l'image et afficher le placeholder
  const img = event.target;
  console.error('[Bookmarks] Image load error:', {
    src: img?.src,
    error: event,
    bookmark: bookmarks.value.find(b => b.thumbnail_url === img?.src),
  });
  if (img && img.parentElement) {
    img.style.display = 'none';
  }
};

const handleImageLoad = event => {
  // Image chargée avec succès
  const img = event.target;
  console.log('[Bookmarks] Image loaded successfully:', img?.src);
  if (img && img.parentElement) {
    img.style.display = 'block';
  }
};

const openBookmark = (bookmark, event) => {
  if (!bookmark) return;
  if (event) {
    event.preventDefault();
    event.stopPropagation();
  }
  selectedBookmark.value = { ...bookmark };
  showBookmarkDrawer.value = true;
};

const closeBookmarkDrawer = () => {
  showBookmarkDrawer.value = false;
  selectedBookmark.value = null;
};

const handleEditFromDrawer = bookmark => {
  openEditModal(bookmark);
};

const handleDeleteFromDrawer = bookmark => {
  openDeleteModal(bookmark);
};

const setSortBy = (value, order = 'desc') => {
  sortBy.value = value;
  sortOrder.value = order;
  showSortMenu.value = false;
  fetchBookmarks();
};

const setKeywordFilter = keyword => {
  keywordFilter.value = keywordFilter.value === keyword ? '' : keyword;
  showFilterMenu.value = false;
};

const toggleTag = tag => {
  const index = selectedTags.value.findIndex(
    t => t.toLowerCase() === tag.toLowerCase()
  );
  if (index > -1) {
    selectedTags.value.splice(index, 1);
  } else {
    selectedTags.value.push(tag);
  }
};

const isTagSelected = tag => {
  return selectedTags.value.some(t => t.toLowerCase() === tag.toLowerCase());
};

const clearAllTags = () => {
  selectedTags.value = [];
};

const toggleSortMenu = event => {
  event?.stopPropagation();
  showSortMenu.value = !showSortMenu.value;
  showFilterMenu.value = false;
};

const toggleFilterMenu = event => {
  event?.stopPropagation();
  showFilterMenu.value = !showFilterMenu.value;
  showSortMenu.value = false;
};

const closeAllMenus = () => {
  showSortMenu.value = false;
  showFilterMenu.value = false;
  closeMenu();
};

const formatDate = dateString => {
  const date = new Date(dateString);
  return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
};

onMounted(() => {
  fetchBookmarks();
  document.addEventListener('click', closeAllMenus);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', closeAllMenus);
});
</script>

<template>
  <section class="flex flex-col w-full h-full overflow-hidden bg-n-background">
    <!-- Header -->
    <header class="sticky top-0 z-10 bg-n-background border-b border-n-weak">
      <div
        class="flex items-start sm:items-center justify-between w-full py-6 px-6 gap-2 mx-auto max-w-[60rem]"
      >
        <span class="text-xl font-medium truncate text-n-slate-12">
          {{ t('BOOKMARKS.TITLE') }}
        </span>

        <div class="flex items-center flex-col sm:flex-row flex-shrink-0 gap-4">
          <!-- Recherche -->
          <div class="flex items-center gap-2 w-full">
            <Input
              :model-value="searchQuery"
              type="search"
              :placeholder="t('BOOKMARKS.SEARCH_PLACEHOLDER')"
              :custom-input-class="[
                'h-8 [&:not(.focus)]:!border-transparent bg-n-alpha-2 dark:bg-n-solid-1 ltr:!pl-8 !py-1 rtl:!pr-8',
              ]"
              class="w-full"
              @input="searchQuery = $event.target.value"
            >
              <template #prefix>
                <Icon
                  icon="i-lucide-search"
                  class="absolute -translate-y-1/2 text-n-slate-11 size-4 top-1/2 ltr:left-2 rtl:right-2"
                />
              </template>
            </Input>
          </div>

          <div class="flex items-center flex-shrink-0 gap-4">
            <!-- Sort & Filter -->
            <div class="flex items-center gap-2">
              <!-- Sort button -->
              <div class="relative">
                <Button
                  icon="i-lucide-arrow-up-down"
                  color="slate"
                  size="sm"
                  variant="ghost"
                  :class="{ 'bg-n-blue-3 text-n-blue-11': showSortMenu }"
                  @click="toggleSortMenu"
                />
                <!-- Sort menu -->
                <div
                  v-if="showSortMenu"
                  class="absolute top-full right-0 mt-1 p-1 rounded-md shadow-xl bg-n-alpha-3/50 backdrop-blur-[100px] outline-1 outline outline-n-weak/50 min-w-[200px] z-[9999]"
                  @click.stop
                >
                  <button
                    class="w-full min-h-7 flex items-center flex-nowrap p-1 rounded-md overflow-hidden text-xs text-n-slate-12 hover:bg-n-brand hover:text-white cursor-pointer"
                    :class="{ 'bg-n-brand text-white': sortBy === 'title' }"
                    @click="setSortBy('title', 'asc')"
                  >
                    <span class="i-lucide-text size-3.5 flex-shrink-0" />
                    <span class="mx-2 truncate flex-1 text-left">{{
                      t('BOOKMARKS.SORT.TITLE')
                    }}</span>
                  </button>
                  <button
                    class="w-full min-h-7 flex items-center flex-nowrap p-1 rounded-md overflow-hidden text-xs text-n-slate-12 hover:bg-n-brand hover:text-white cursor-pointer"
                    :class="{
                      'bg-n-brand text-white': sortBy === 'updated_at',
                    }"
                    @click="setSortBy('updated_at', 'desc')"
                  >
                    <span class="i-lucide-clock size-3.5 flex-shrink-0" />
                    <span class="mx-2 truncate flex-1 text-left">{{
                      t('BOOKMARKS.SORT.DATE')
                    }}</span>
                  </button>
                  <button
                    class="w-full min-h-7 flex items-center flex-nowrap p-1 rounded-md overflow-hidden text-xs text-n-slate-12 hover:bg-n-brand hover:text-white cursor-pointer"
                    :class="{ 'bg-n-brand text-white': sortBy === 'url' }"
                    @click="setSortBy('url', 'asc')"
                  >
                    <span class="i-lucide-link size-3.5 flex-shrink-0" />
                    <span class="mx-2 truncate flex-1 text-left">{{
                      t('BOOKMARKS.SORT.URL')
                    }}</span>
                  </button>
                </div>
              </div>

              <!-- Filter button -->
              <div class="relative">
                <Button
                  icon="i-lucide-filter"
                  color="slate"
                  size="sm"
                  variant="ghost"
                  :class="{
                    'bg-n-blue-3 text-n-blue-11':
                      showFilterMenu || keywordFilter !== '',
                  }"
                  @click="toggleFilterMenu"
                />
                <!-- Filter menu -->
                <div
                  v-if="showFilterMenu"
                  class="absolute top-full right-0 mt-1 p-1 rounded-md shadow-xl bg-n-alpha-3/50 backdrop-blur-[100px] outline-1 outline outline-n-weak/50 min-w-[200px] z-50 max-h-[300px] overflow-y-auto"
                  @click.stop
                >
                  <button
                    class="w-full min-h-7 flex items-center flex-nowrap p-1 rounded-md overflow-hidden text-xs text-n-slate-12 hover:bg-n-brand hover:text-white cursor-pointer"
                    :class="{
                      'bg-n-brand text-white': keywordFilter === '',
                    }"
                    @click="setKeywordFilter('')"
                  >
                    <span class="i-lucide-tag size-3.5 flex-shrink-0" />
                    <span class="mx-2 truncate flex-1 text-left">{{
                      t('BOOKMARKS.FILTER.ALL')
                    }}</span>
                  </button>
                  <hr class="m-1 rounded border-b border-n-weak" />
                  <button
                    v-for="keyword in allKeywords"
                    :key="keyword"
                    class="w-full min-h-7 flex items-center flex-nowrap p-1 rounded-md overflow-hidden text-xs text-n-slate-12 hover:bg-n-brand hover:text-white cursor-pointer"
                    :class="{
                      'bg-n-brand text-white': keywordFilter === keyword,
                    }"
                    @click="setKeywordFilter(keyword)"
                  >
                    <span class="i-lucide-tag size-3.5 flex-shrink-0" />
                    <span class="mx-2 truncate flex-1 text-left">{{
                      keyword
                    }}</span>
                  </button>
                </div>
              </div>
            </div>

            <div class="w-px h-4 bg-n-strong" />

            <div class="flex items-center gap-2">
              <!-- Toggle view -->
              <div class="relative">
                <Button
                  icon="i-lucide-list"
                  color="slate"
                  size="sm"
                  variant="ghost"
                  :class="{ 'bg-n-blue-3 text-n-blue-11': viewMode === 'list' }"
                  @click="viewMode = 'list'"
                />
              </div>
              <div class="relative">
                <Button
                  icon="i-lucide-layout-grid"
                  color="slate"
                  size="sm"
                  variant="ghost"
                  :class="{ 'bg-n-blue-3 text-n-blue-11': viewMode === 'grid' }"
                  @click="viewMode = 'grid'"
                />
              </div>
            </div>

            <div class="w-px h-4 bg-n-strong" />

            <!-- Add button -->
            <Button
              :label="t('BOOKMARKS.ADD')"
              icon="i-lucide-plus"
              size="sm"
              @click="openCreateModal"
            />
          </div>
        </div>
      </div>

      <!-- Tags filter scroll horizontal -->
      <div
        v-if="allTags.length > 0"
        class="w-full overflow-x-auto no-scrollbar border-t border-n-weak bg-n-alpha-1"
      >
        <div class="flex items-center gap-2 px-6 py-3 min-w-max">
          <button
            v-if="selectedTags.length > 0"
            class="flex-shrink-0 px-3 py-1.5 text-xs font-medium rounded-full bg-n-slate-3 dark:bg-n-slate-4 text-n-slate-11 hover:bg-n-slate-4 dark:hover:bg-n-slate-5 transition-colors flex items-center gap-1.5"
            @click="clearAllTags"
          >
            <span class="i-lucide-x size-3" />
            {{ t('BOOKMARKS.CLEAR_FILTERS') }}
          </button>
          <button
            v-for="tag in allTags"
            :key="tag"
            class="flex-shrink-0 px-3 py-1.5 text-xs font-medium rounded-full transition-all cursor-pointer flex items-center gap-1.5"
            :class="
              isTagSelected(tag)
                ? 'bg-n-brand text-white shadow-md'
                : 'bg-n-alpha-2 dark:bg-n-alpha-3 text-n-slate-11 hover:bg-n-alpha-3 dark:hover:bg-n-alpha-4'
            "
            @click="toggleTag(tag)"
          >
            <span v-if="isTagSelected(tag)" class="i-lucide-check size-3" />
            {{ tag }}
          </button>
        </div>
      </div>
    </header>

    <!-- Main content -->
    <main class="flex-1 px-6 overflow-y-auto relative">
      <div class="w-full max-w-[60rem] h-full mx-auto py-4">
        <!-- Loading -->
        <div
          v-if="loading"
          class="flex items-center justify-center py-10 text-n-slate-11"
        >
          <Spinner />
        </div>

        <!-- Empty state -->
        <div
          v-else-if="isEmpty"
          class="flex flex-col items-center justify-center py-24 rounded-lg border-2 bg-gradient-to-br from-n-solid-1 to-n-slate-2 dark:to-n-slate-1 border-dashed border-n-weak"
        >
          <div
            class="mb-6 w-20 h-20 rounded-full bg-n-blue-2 dark:bg-n-blue-3 flex items-center justify-center"
          >
            <span class="i-lucide-bookmark size-10 text-n-blue-10" />
          </div>
          <h3 class="text-lg font-semibold text-n-slate-12 mb-2">
            {{ t('BOOKMARKS.EMPTY_STATE.TITLE') }}
          </h3>
          <p class="text-sm text-center text-n-slate-10 max-w-md mb-6">
            {{ t('BOOKMARKS.EMPTY_STATE.DESCRIPTION') }}
          </p>
          <Button
            :label="t('BOOKMARKS.ADD')"
            icon="i-lucide-plus"
            size="sm"
            @click="openCreateModal"
          />
        </div>

        <!-- Bookmarks content -->
        <div v-else>
          <!-- Grid view -->
          <div
            v-if="viewMode === 'grid'"
            class="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 p-6"
          >
            <div
              v-for="bookmark in filteredBookmarks"
              :key="bookmark.id"
              role="button"
              tabindex="0"
              class="flex flex-col rounded-lg bg-n-alpha-1 dark:bg-n-alpha-2 border border-n-weak group cursor-pointer animate-fade-in hover:bg-n-blue-2 dark:hover:bg-n-blue-3 hover:border-n-blue-6 transition-all duration-150 relative"
              @click="openBookmark(bookmark, $event)"
              @keydown.enter="openBookmark(bookmark, $event)"
            >
              <!-- Thumbnail -->
              <div
                class="w-full h-32 rounded-t-lg overflow-hidden bg-n-alpha-2 relative flex-shrink-0"
              >
                <img
                  v-if="bookmark.thumbnail_url"
                  :src="bookmark.thumbnail_url"
                  :alt="bookmark.title"
                  class="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                  referrerpolicy="no-referrer"
                  @error="handleImageError($event)"
                  @load="handleImageLoad"
                />
                <div
                  v-else
                  class="w-full h-full flex items-center justify-center bg-n-alpha-2 group-hover:bg-n-alpha-3 transition-colors"
                >
                  <span class="i-lucide-globe size-8 text-n-slate-10" />
                </div>
              </div>

              <!-- Content -->
              <div
                class="flex flex-col p-3 gap-1.5 flex-shrink-0 min-h-[120px]"
              >
                <div class="flex items-start justify-between gap-2 min-w-0">
                  <h3
                    class="text-sm font-semibold text-n-slate-12 line-clamp-2 leading-snug flex-1 min-w-0"
                    :title="bookmark.title"
                  >
                    {{ bookmark.title }}
                  </h3>

                  <!-- Menu 3 points -->
                  <div class="flex-shrink-0 relative -mr-1 -mt-1 z-[10000]">
                    <button
                      class="p-1 rounded opacity-0 group-hover:opacity-100 focus:opacity-100 hover:bg-n-alpha-2 text-n-slate-10 hover:text-n-slate-12 transition-all relative z-[10001]"
                      :class="{ 'opacity-100': openMenuId === bookmark.id }"
                      @click.stop="toggleMenu(bookmark.id, $event)"
                    >
                      <span class="i-lucide-more-vertical size-4" />
                    </button>

                    <!-- Dropdown actions -->
                    <div
                      v-if="openMenuId === bookmark.id"
                      class="absolute top-full right-0 mt-1 p-1 rounded-lg shadow-2xl bg-n-solid-1 dark:bg-n-solid-2 border border-n-strong min-w-[200px] z-[10002]"
                      @click.stop
                    >
                      <button
                        class="w-full min-h-7 flex items-center flex-nowrap p-1 rounded-md overflow-hidden text-xs text-n-slate-12 hover:bg-n-brand hover:text-white cursor-pointer"
                        @click="
                          openEditModal(bookmark);
                          closeMenu();
                        "
                      >
                        <span class="i-lucide-pencil size-3.5 flex-shrink-0" />
                        <span class="mx-2 truncate flex-1 text-left">{{
                          t('BOOKMARKS.EDIT')
                        }}</span>
                      </button>
                      <hr class="m-1 rounded border-b border-n-weak" />
                      <button
                        class="w-full min-h-7 flex items-center flex-nowrap p-1 rounded-md overflow-hidden text-xs text-n-slate-12 hover:bg-n-brand hover:text-white cursor-pointer"
                        @click="
                          openDeleteModal(bookmark);
                          closeMenu();
                        "
                      >
                        <span class="i-lucide-trash-2 size-3.5 flex-shrink-0" />
                        <span class="mx-2 truncate flex-1 text-left">{{
                          t('BOOKMARKS.DELETE')
                        }}</span>
                      </button>
                    </div>
                  </div>
                </div>

                <p
                  v-if="bookmark.description"
                  class="text-xs text-n-slate-10 line-clamp-2 break-words"
                >
                  {{ bookmark.description }}
                </p>

                <div
                  v-if="
                    bookmark.keywords_array &&
                    bookmark.keywords_array.length > 0
                  "
                  class="flex flex-wrap gap-1"
                >
                  <span
                    v-for="keyword in bookmark.keywords_array.slice(0, 3)"
                    :key="keyword"
                    class="px-2 py-0.5 text-[10px] bg-n-blue-2 dark:bg-n-blue-3 text-n-blue-11 rounded font-medium"
                  >
                    {{ keyword }}
                  </span>
                </div>

                <div class="flex items-center gap-1.5 min-w-0 mt-1">
                  <span
                    class="i-lucide-link size-3 text-n-blue-9 flex-shrink-0"
                  />
                  <a
                    :href="bookmark.url"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="text-xs text-n-blue-10 hover:text-n-blue-11 truncate flex-1 min-w-0"
                    @click.stop
                  >
                    {{ bookmark.url }}
                  </a>
                </div>
              </div>
            </div>
          </div>

          <!-- List view -->
          <div v-if="viewMode === 'list'" class="flex flex-col gap-2 p-6">
            <div
              v-for="bookmark in filteredBookmarks"
              :key="bookmark.id"
              class="flex items-center gap-4 px-4 py-3 rounded-lg bg-n-alpha-1 dark:bg-n-alpha-2 border border-n-weak hover:bg-n-blue-2 dark:hover:bg-n-blue-3 hover:border-n-blue-6 cursor-pointer group relative transition-all duration-150"
              @click="openBookmark(bookmark, $event)"
            >
              <!-- Thumbnail -->
              <div
                class="flex-shrink-0 w-20 h-20 rounded overflow-hidden bg-n-alpha-2"
              >
                <img
                  v-if="bookmark.thumbnail_url"
                  :src="bookmark.thumbnail_url"
                  :alt="bookmark.title"
                  class="w-full h-full object-cover"
                />
                <div
                  v-else
                  class="w-full h-full flex items-center justify-center bg-n-alpha-2"
                >
                  <span class="i-lucide-globe size-6 text-n-slate-10" />
                </div>
              </div>

              <!-- Content -->
              <div class="flex-1 min-w-0">
                <h3 class="text-sm font-semibold text-n-slate-12 truncate">
                  {{ bookmark.title }}
                </h3>
                <p
                  v-if="bookmark.description"
                  class="text-xs text-n-slate-10 line-clamp-1 mt-1"
                >
                  {{ bookmark.description }}
                </p>
                <div class="flex items-center gap-2 mt-1">
                  <a
                    :href="bookmark.url"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="text-xs text-n-blue-10 hover:text-n-blue-11 truncate"
                    @click.stop
                  >
                    {{ bookmark.url }}
                  </a>
                </div>
                <div
                  v-if="
                    bookmark.keywords_array &&
                    bookmark.keywords_array.length > 0
                  "
                  class="flex flex-wrap gap-1 mt-1"
                >
                  <span
                    v-for="keyword in bookmark.keywords_array.slice(0, 5)"
                    :key="keyword"
                    class="px-2 py-0.5 text-xs bg-n-blue-2 dark:bg-n-blue-3 text-n-blue-11 rounded"
                  >
                    {{ keyword }}
                  </span>
                </div>
              </div>

              <!-- Date -->
              <span
                class="text-sm text-n-slate-10 w-32 text-right hidden md:block"
              >
                {{ formatDate(bookmark.updated_at) }}
              </span>

              <!-- Menu 3 points -->
              <div class="flex-shrink-0 relative z-[10000]">
                <button
                  class="p-1 rounded hover:bg-n-alpha-2 text-n-slate-10 hover:text-n-slate-12 transition-colors relative z-[10001]"
                  @click.stop="toggleMenu(bookmark.id, $event)"
                >
                  <span class="i-lucide-more-vertical size-4" />
                </button>

                <!-- Dropdown actions -->
                <div
                  v-if="openMenuId === bookmark.id"
                  class="absolute top-full right-0 mt-1 p-1 rounded-lg shadow-2xl bg-n-solid-1 dark:bg-n-solid-2 border border-n-strong min-w-[200px] z-[10002]"
                  @click.stop
                >
                  <button
                    class="w-full min-h-7 flex items-center flex-nowrap p-1 rounded-md overflow-hidden text-xs text-n-slate-12 hover:bg-n-brand hover:text-white cursor-pointer"
                    @click="
                      openEditModal(bookmark);
                      closeMenu();
                    "
                  >
                    <span class="i-lucide-pencil size-3.5 flex-shrink-0" />
                    <span class="mx-2 truncate flex-1 text-left">{{
                      t('BOOKMARKS.EDIT')
                    }}</span>
                  </button>
                  <hr class="m-1 rounded border-b border-n-weak" />
                  <button
                    class="w-full min-h-7 flex items-center flex-nowrap p-1 rounded-md overflow-hidden text-xs text-n-slate-12 hover:bg-n-brand hover:text-white cursor-pointer"
                    @click="
                      openDeleteModal(bookmark);
                      closeMenu();
                    "
                  >
                    <span class="i-lucide-trash-2 size-3.5 flex-shrink-0" />
                    <span class="mx-2 truncate flex-1 text-left">{{
                      t('BOOKMARKS.DELETE')
                    }}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Create modal -->
      <Dialog
        v-if="showCreateModal"
        ref="createDialog"
        type="edit"
        width="md"
        :title="t('BOOKMARKS.CREATE_MODAL.TITLE')"
        :description="t('BOOKMARKS.CREATE_MODAL.DESCRIPTION')"
        :confirm-button-label="t('BOOKMARKS.CREATE_MODAL.CONFIRM')"
        @confirm="createBookmark"
        @close="closeCreateModal"
      >
        <div class="flex flex-col gap-4">
          <Input
            v-model="formTitle"
            :label="t('BOOKMARKS.FORM.TITLE')"
            :placeholder="t('BOOKMARKS.FORM.TITLE_PLACEHOLDER')"
            required
          />
          <Input
            v-model="formUrl"
            :label="t('BOOKMARKS.FORM.URL')"
            :placeholder="t('BOOKMARKS.FORM.URL_PLACEHOLDER')"
            type="url"
            required
          />
          <Input
            v-model="formDescription"
            :label="t('BOOKMARKS.FORM.DESCRIPTION')"
            :placeholder="t('BOOKMARKS.FORM.DESCRIPTION_PLACEHOLDER')"
            type="textarea"
          />
          <Input
            v-model="formKeywords"
            :label="t('BOOKMARKS.FORM.KEYWORDS')"
            :placeholder="t('BOOKMARKS.FORM.KEYWORDS_PLACEHOLDER')"
          />
          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-2">
              {{ t('BOOKMARKS.FORM.THUMBNAIL') }}
            </label>
            <input
              ref="thumbnailInput"
              type="file"
              accept="image/*"
              class="block w-full text-sm text-n-slate-11 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-n-blue-2 file:text-n-blue-11 hover:file:bg-n-blue-3"
              @change="handleThumbnailChange"
            />
          </div>
        </div>
      </Dialog>

      <!-- Edit modal -->
      <Dialog
        v-if="showEditModal"
        ref="editDialog"
        type="edit"
        width="md"
        :title="t('BOOKMARKS.EDIT_MODAL.TITLE')"
        :description="t('BOOKMARKS.EDIT_MODAL.DESCRIPTION')"
        :confirm-button-label="t('BOOKMARKS.EDIT_MODAL.CONFIRM')"
        @confirm="updateBookmark"
        @close="closeEditModal"
      >
        <div class="flex flex-col gap-4">
          <Input
            v-model="formTitle"
            :label="t('BOOKMARKS.FORM.TITLE')"
            :placeholder="t('BOOKMARKS.FORM.TITLE_PLACEHOLDER')"
            required
          />
          <Input
            v-model="formUrl"
            :label="t('BOOKMARKS.FORM.URL')"
            :placeholder="t('BOOKMARKS.FORM.URL_PLACEHOLDER')"
            type="url"
            required
          />
          <Input
            v-model="formDescription"
            :label="t('BOOKMARKS.FORM.DESCRIPTION')"
            :placeholder="t('BOOKMARKS.FORM.DESCRIPTION_PLACEHOLDER')"
            type="textarea"
          />
          <Input
            v-model="formKeywords"
            :label="t('BOOKMARKS.FORM.KEYWORDS')"
            :placeholder="t('BOOKMARKS.FORM.KEYWORDS_PLACEHOLDER')"
          />
          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-2">
              {{ t('BOOKMARKS.FORM.THUMBNAIL') }}
            </label>
            <input
              ref="thumbnailInput"
              type="file"
              accept="image/*"
              class="block w-full text-sm text-n-slate-11 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-n-blue-2 file:text-n-blue-11 hover:file:bg-n-blue-3"
              @change="handleThumbnailChange"
            />
            <p
              v-if="bookmarkToEdit?.thumbnail_url"
              class="text-xs text-n-slate-10 mt-1"
            >
              {{ t('BOOKMARKS.FORM.THUMBNAIL_CURRENT') }}
            </p>
          </div>
        </div>
      </Dialog>

      <!-- Delete confirmation modal -->
      <Dialog
        v-if="showDeleteModal"
        ref="deleteDialog"
        type="alert"
        width="md"
        :title="t('BOOKMARKS.DELETE_MODAL.TITLE')"
        :description="
          t('BOOKMARKS.DELETE_MODAL.DESCRIPTION', {
            title: bookmarkToDelete?.title,
          })
        "
        :confirm-button-label="t('BOOKMARKS.DELETE_MODAL.CONFIRM')"
        @confirm="deleteBookmark"
        @close="closeDeleteModal"
      />

      <!-- Bookmark Drawer -->
      <BookmarkDrawer
        :show="showBookmarkDrawer"
        :bookmark="selectedBookmark"
        @close="closeBookmarkDrawer"
        @edit="handleEditFromDrawer"
        @delete="handleDeleteFromDrawer"
      />
    </main>
  </section>
</template>

<style scoped>
@keyframes fade-in {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.animate-fade-in {
  animation: fade-in 150ms ease-in-out;
}

.line-clamp-1 {
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
